import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useFocusItem } from '../../hooks/use_focus_item.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { bindRefs } from '../../utils/react.js';
import { Box } from '../Box/Box.js';
import { Button } from '../Button/Button.js';
import { IconCalendar } from '../Icons/IconCalendar.js';
import { IconClose } from '../Icons/IconClose.js';
import { InputContainer } from '../Input/InputContainer.js';
import { Popper } from '../Popper/Popper.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["closeButtonProps", "closeOnSelect", "containerSelector", "disabled", "inline", "locale", "max", "min", "onBlur", "openTo", "renderDatePicker", "renderSpinButton", "zIndex"];
var DateInputButton = styled(Button).withConfig({
  displayName: "DateInputBase__DateInputButton",
  componentId: "sc-1vu0qox-0"
})(["&&{padding:0;min-height:0;min-width:0;margin-top:1px;}&&:disabled{background:transparent;}&&:focus,&&:hover{background:transparent;color:", ";}"], function (props) {
  return props.theme.CTAColors.primary;
});
var DateInputInputContainer = styled(InputContainer).withConfig({
  displayName: "DateInputBase__DateInputInputContainer",
  componentId: "sc-1vu0qox-1"
})(["&&{cursor:", ";}"], function (_ref) {
  var disabled = _ref.disabled;
  return disabled ? "not-allowed" : "pointer";
});
var DateInputBase = /*#__PURE__*/React.forwardRef(function DateInputBase2(props, ref) {
  var closeButtonProps = props.closeButtonProps,
    closeOnSelect = props.closeOnSelect,
    containerSelector = props.containerSelector,
    disabled = props.disabled,
    _props$inline = props.inline,
    inline = _props$inline === void 0 ? true : _props$inline,
    locale = props.locale,
    max = props.max,
    min = props.min,
    onBlur = props.onBlur,
    openTo = props.openTo,
    renderDatePicker = props.renderDatePicker,
    renderSpinButton = props.renderSpinButton,
    zIndex = props.zIndex,
    inputContainerProps = _objectWithoutProperties(props, _excluded);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showing = _React$useState2[0],
    setShowing = _React$useState2[1];
  var datesRef = React.useRef(null);
  var buttonRef = React.useRef(null);
  var _useFocusItem = useFocusItem({
      datesRef: datesRef,
      buttonRef: buttonRef
    }),
    focusItem = _useFocusItem.focusItem,
    itemKeys = _useFocusItem.itemKeys,
    setFocusItem = _useFocusItem.setFocusItem;
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var handleClick = function handleClick(e) {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setFocusItem(showing ? void 0 : itemKeys.datesRef);
    setShowing(!showing);
  };
  var onOutsideClick = function onOutsideClick() {
    setShowing(false);
    if (onBlur) {
      var event = {
        target: datesRef.current
      };
      onBlur(event);
    }
  };
  var onSelectionComplete = function onSelectionComplete() {
    if (!closeOnSelect) return;
    setTimeout(function () {
      setShowing(false);
    }, 500);
  };
  return /*#__PURE__*/jsx(Popper, {
    containerSelector: containerSelector,
    inline: inline,
    zIndex: zIndex,
    onOutsideClick: onOutsideClick,
    onHide: focusItem,
    onShow: focusItem,
    renderContent: function renderContent(_ref2) {
      var contentRef = _ref2.ref,
        style = _ref2.style;
      return /*#__PURE__*/jsx(Box, {
        "aria-label": t("DateInputBase.dialogLabel"),
        borderRadius: 2,
        elevation: 2,
        onKeyDown: function onKeyDown(e) {
          if (e.key === KEYS.ESCAPE) {
            setFocusItem(itemKeys.buttonRef);
            setShowing(false);
          }
        },
        ref: contentRef,
        role: "dialog",
        style: _objectSpread({}, style),
        children: renderDatePicker({
          disabled: disabled,
          locale: locale,
          max: max,
          min: min,
          openTo: openTo,
          ref: datesRef,
          onSelectionComplete: onSelectionComplete
        })
      });
    },
    renderTrigger: function renderTrigger(_ref3) {
      var triggerRef = _ref3.ref;
      return /*#__PURE__*/jsx(DateInputInputContainer, _objectSpread(_objectSpread({}, inputContainerProps), {}, {
        disabled: props.disabled,
        endAdornment: /*#__PURE__*/jsx(DateInputButton, _objectSpread(_objectSpread({}, closeButtonProps), {}, {
          disabled: disabled,
          hideLabel: true,
          label: t("DateInputBase.closeButtonLabel"),
          startAdornment: IconClose,
          variant: "ghost"
        })),
        onClick: handleClick,
        startAdornment: /*#__PURE__*/jsx(DateInputButton, {
          disabled: disabled,
          "aria-expanded": showing,
          "aria-haspopup": "dialog",
          hideLabel: true,
          onClick: handleClick,
          label: t("DateInputBase.calendarButtonLabel"),
          ref: buttonRef,
          startAdornment: IconCalendar,
          variant: "ghost"
        }),
        ref: bindRefs(ref, triggerRef),
        role: "group",
        children: renderSpinButton({
          disabled: disabled
        })
      }));
    },
    showing: showing
  });
});
DateInputBase.displayName = "DateInputBase";

export { DateInputBase };
